import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import TextAlign from "@tiptap/extension-text-align"
import Image from "@tiptap/extension-image"
import Youtube from "@tiptap/extension-youtube"

const TOOLBAR_BUTTONS = [
  {
    group: [
      { action: "toggleBold",    label: "<strong>G</strong>", title: "Gras" },
      { action: "toggleItalic",  label: "<em>I</em>",         title: "Italique" },
      { action: "toggleStrike",  label: "<s>S</s>",           title: "Barré" },
    ]
  },
  {
    group: [
      { action: "h2", label: "H2", title: "Titre 2" },
      { action: "h3", label: "H3", title: "Titre 3" },
      { action: "h4", label: "H4", title: "Titre 4" },
    ]
  },
  {
    group: [
      { action: "toggleBulletList",  label: "• —", title: "Liste à puces" },
      { action: "toggleOrderedList", label: "1 —", title: "Liste numérotée" },
    ]
  },
  {
    group: [
      { action: "alignLeft",   label: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>`, title: "Aligner à gauche" },
      { action: "alignCenter", label: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>`, title: "Centrer" },
      { action: "alignRight",  label: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>`, title: "Aligner à droite" },
    ]
  },
  {
    group: [
      { action: "toggleBlockquote",  label: "❝", title: "Citation" },
      { action: "setHorizontalRule", label: "—",  title: "Séparateur" },
    ]
  },
  {
    group: [
      { action: "insertImage",   label: "🖼", title: "Image" },
      { action: "insertYoutube", label: "▶",  title: "Vidéo YouTube / Vimeo" },
    ]
  },
]

// Convertit les iframes YouTube/Vimeo brutes en wrapper reconnu par TipTap
function normalizeYoutubeIframes(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")

  doc.querySelectorAll("iframe").forEach((iframe) => {
    const src = iframe.getAttribute("src") || ""
    const isYoutube = src.includes("youtube") || src.includes("youtu.be")
    const isVimeo = src.includes("vimeo")

    if (isYoutube || isVimeo) {
      const parent = iframe.parentElement
      if (parent && !parent.hasAttribute("data-youtube-video")) {
        parent.setAttribute("data-youtube-video", "")
      }
    }
  })

  return doc.body.innerHTML
}

export default class extends Controller {
  static targets = ["toolbar", "holder", "input"]

  connect() {
    const rawContent = this.inputTarget.value || ""
    const existingContent = normalizeYoutubeIframes(rawContent)

    this.editor = new Editor({
      element: this.holderTarget,
      extensions: [
        StarterKit,
        TextAlign.configure({ types: ["heading", "paragraph", "image"] }),
        Image.configure({ inline: false, allowBase64: false }),
        Youtube.configure({ controls: true, nocookie: true, allowFullscreen: true }),
      ],
      content: existingContent,
      onUpdate: () => {
        this.inputTarget.value = this.editor.getHTML()
        this.updateToolbar()
      },
      onSelectionUpdate: () => {
        this.updateToolbar()
      },
    })

    this.buildToolbar()
    this.updateToolbar()
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }

  buildToolbar() {
    const toolbar = this.toolbarTarget
    toolbar.innerHTML = ""

    TOOLBAR_BUTTONS.forEach(({ group }, groupIndex) => {
      if (groupIndex > 0) {
        const sep = document.createElement("span")
        sep.className = "toolbar-sep"
        toolbar.appendChild(sep)
      }

      group.forEach(({ action, label, title }) => {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.innerHTML = label
        btn.title = title
        btn.dataset.action = action
        btn.className = "toolbar-btn"
        btn.addEventListener("click", (e) => {
          e.preventDefault()
          this.handleAction(action)
        })
        toolbar.appendChild(btn)
      })
    })
  }

  handleAction(action) {
    const chain = this.editor.chain().focus()

    switch (action) {
      case "toggleBold":          chain.toggleBold().run(); break
      case "toggleItalic":        chain.toggleItalic().run(); break
      case "toggleStrike":        chain.toggleStrike().run(); break
      case "h2":                  chain.toggleHeading({ level: 2 }).run(); break
      case "h3":                  chain.toggleHeading({ level: 3 }).run(); break
      case "h4":                  chain.toggleHeading({ level: 4 }).run(); break
      case "toggleBulletList":    chain.toggleBulletList().run(); break
      case "toggleOrderedList":   chain.toggleOrderedList().run(); break
      case "alignLeft":           chain.setTextAlign("left").run(); break
      case "alignCenter":         chain.setTextAlign("center").run(); break
      case "alignRight":          chain.setTextAlign("right").run(); break
      case "toggleBlockquote":    chain.toggleBlockquote().run(); break
      case "setHorizontalRule":   chain.setHorizontalRule().run(); break
      case "insertImage":         this.promptImage(); break
      case "insertYoutube":       this.promptYoutube(); break
    }

    this.updateToolbar()
  }

  promptImage() {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.addEventListener("change", async (e) => {
      const file = e.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append("image", file)

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch("/uploads/images", {
        method: "POST",
        headers: { "X-CSRF-Token": token },
        body: formData,
      })
      const data = await response.json()
      if (data.success && data.file?.url) {
        this.editor.chain().focus().setImage({ src: data.file.url }).run()
      }
    })
    input.click()
  }

  promptYoutube() {
    const url = window.prompt("URL YouTube ou Vimeo :")
    if (url) {
      this.editor.chain().focus().setYoutubeVideo({ src: url }).run()
    }
  }

  updateToolbar() {
    this.toolbarTarget.querySelectorAll(".toolbar-btn").forEach((btn) => {
      const action = btn.dataset.action
      let isActive = false

      switch (action) {
        case "toggleBold":        isActive = this.editor.isActive("bold"); break
        case "toggleItalic":      isActive = this.editor.isActive("italic"); break
        case "toggleStrike":      isActive = this.editor.isActive("strike"); break
        case "h2":                isActive = this.editor.isActive("heading", { level: 2 }); break
        case "h3":                isActive = this.editor.isActive("heading", { level: 3 }); break
        case "h4":                isActive = this.editor.isActive("heading", { level: 4 }); break
        case "toggleBulletList":  isActive = this.editor.isActive("bulletList"); break
        case "toggleOrderedList": isActive = this.editor.isActive("orderedList"); break
        case "alignLeft":         isActive = this.editor.isActive({ textAlign: "left" }); break
        case "alignCenter":       isActive = this.editor.isActive({ textAlign: "center" }); break
        case "alignRight":        isActive = this.editor.isActive({ textAlign: "right" }); break
        case "toggleBlockquote":  isActive = this.editor.isActive("blockquote"); break
      }

      btn.classList.toggle("is-active", isActive)
    })
  }
}
